/**
 * Importes en euros escritos a la española (tarifario de colaboradores,
 * 7/10/2026).
 *
 * `leerImporte` acepta lo que se teclea en la clínica: «12,50», «12.50»,
 * «1.234,56», «1.234», «12 €». Devuelve el número, `null` si está vacío o
 * `NaN` si no se entiende (lo dudoso no se adivina).
 *
 * EL MISMO CRITERIO QUE EL SERVIDOR (revisión del 7/10/2026;
 * backend/routers/colaboradores_router.py, `_numero_escrito`). Antes el
 * servidor leía «1.500» como 1,5 y la pantalla como 1500. Ahora, en los dos:
 *  - coma o punto con una o dos cifras detrás es decimal: «12,5», «12.50»,
 *    «0.5»;
 *  - punto con grupos de tres cifras, y el primer grupo de 1 a 9 cifras sin
 *    cero delante, es de miles: «1.500» (1500), «12.345» (12345). «0.125» NO
 *    es de miles (sería «cero mil»): es dudoso;
 *  - punto de miles y coma decimal: «1.234,56»;
 *  - cualquier otra cosa con tres o más cifras tras el punto o la coma
 *    («12,345», «0.125», «1.234,567»), o que no sea un número («doce»,
 *    «-3», «inf»), es dudosa: NaN.
 *
 * `leerCantidad` es para las CANTIDADES (viales): sin miles. «0.25», «0,25»
 * y «1,5» son decimales; con tres cifras tras el separador («0.250»,
 * «1.000») es dudosa (NaN): nadie pone mil viales, y «0.250» como 250 sería
 * un coste cien veces mayor.
 *
 * `textoEuros` lo enseña siempre igual: «12,50 €», «1.234,56 €» (con punto
 * de miles también en los de cuatro cifras, que el es-ES del navegador no
 * agrupa).
 */
const DECIMAL = /^\d+([.,]\d{1,2})?$/;
const MILES = /^[1-9]\d{0,2}(\.\d{3})+$/;
const MILES_Y_DECIMAL = /^[1-9]\d{0,2}(\.\d{3})+,\d{1,2}$/;

/** Sin espacios ni «€»; null si no queda nada. */
function limpio(valor) {
  const s = String(valor).replace(/€/g, '').replace(/\s+/g, '');
  return s || null;
}

/** Un número que llega como número: vale si es finito y no negativo. */
function numeroValido(valor) {
  return Number.isFinite(valor) && valor >= 0 ? valor : NaN;
}

export function leerImporte(valor) {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === 'number') return numeroValido(valor);
  const s = limpio(valor);
  if (s === null) return null;
  let n = NaN;
  if (DECIMAL.test(s)) n = Number(s.replace(',', '.'));
  else if (MILES.test(s)) n = Number(s.replace(/\./g, ''));
  else if (MILES_Y_DECIMAL.test(s)) n = Number(s.replace(/\./g, '').replace(',', '.'));
  return numeroValido(n);
}

export function leerCantidad(valor) {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === 'number') return numeroValido(valor);
  const s = limpio(valor);
  if (s === null) return null;
  return DECIMAL.test(s) ? numeroValido(Number(s.replace(',', '.'))) : NaN;
}

export function textoEuros(valor) {
  if (valor === null || valor === undefined || valor === '' || Number.isNaN(Number(valor))) return '—';
  const n = Number(valor);
  const [ent, dec] = Math.abs(n).toFixed(2).split('.');
  return `${n < 0 ? '-' : ''}${ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec} €`;
}

/** Para el campo del formulario: «12,50» (sin el símbolo), vacío si no hay. */
export function importeParaCampo(valor) {
  const t = textoEuros(valor);
  return t === '—' ? '' : t.replace(/\s€$/, '');
}

/** Un porcentaje como se lee en la clínica: «45 %», «47,5 %» (reparto de
 *  colaboradores, 7/10/2026). Se escribe con `leerImporte` («47,5»). */
export function textoPorcentaje(valor) {
  if (valor === null || valor === undefined || valor === '' || Number.isNaN(Number(valor))) return '—';
  const n = Math.round(Number(valor) * 100) / 100;
  return `${String(n).replace('.', ',')} %`;
}
